import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const GoalPlannerCalculator = () => {
  const content = article.find((item) => item.id === 6);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [goalType, setGoalType] = useState("savings"); // savings, retirement, education, house

  const [inputs, setInputs] = useState({
    // Basic goal inputs
    goalName: "My Financial Goal",
    goalAmount: "50000",
    currentSavings: "5000",
    timeFrame: "5",
    
    // Contribution inputs
    monthlyContribution: "500",
    contributionIncrease: "3",
    
    // Investment inputs
    expectedReturn: "7",
    inflationRate: "2.5",
    
    // Advanced inputs
    initialContribution: "0",
    oneTimeContributions: "0",
    withdrawalRate: "4",
    
    // Risk management
    riskTolerance: "moderate", // conservative, moderate, aggressive
    probability: "75",
    
    // Milestones
    milestone1: "25",
    milestone2: "50",
    milestone3: "75",
    
    // Common inputs
    currency: "1",
    startDate: new Date().toISOString().split('T')[0]
  });

  const [milestones, setMilestones] = useState([
    { name: "Initial Setup", target: "5000", deadline: "2024-12-31", completed: true },
    { name: "Quarter 1 Target", target: "10000", deadline: "2025-03-31", completed: false },
    { name: "Halfway Point", target: "25000", deadline: "2025-12-31", completed: false },
    { name: "Final Goal", target: "50000", deadline: "2026-12-31", completed: false }
  ]);

  const [scenarios, setScenarios] = useState([
    { return: "5", probability: "25", label: "Conservative" },
    { return: "7", probability: "50", label: "Moderate" },
    { return: "10", probability: "25", label: "Aggressive" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateGoalPlan();
    }
  }, [inputs, milestones, scenarios, goalType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleMilestoneChange = (index, field, value) => {
    const updatedMilestones = milestones.map((milestone, i) => 
      i === index ? { ...milestone, [field]: value } : milestone
    );
    setMilestones(updatedMilestones);
  };

  const handleScenarioChange = (index, field, value) => {
    const updatedScenarios = scenarios.map((scenario, i) => 
      i === index ? { ...scenario, [field]: value } : scenario
    );
    setScenarios(updatedScenarios);
  };

  const addMilestone = () => {
    setMilestones([...milestones, { 
      name: "New Milestone", 
      target: "0", 
      deadline: "2024-12-31", 
      completed: false 
    }]);
  };

  const removeMilestone = (index) => {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((_, i) => i !== index));
    }
  };

  const addScenario = () => {
    setScenarios([...scenarios, { return: "8", probability: "20", label: "Custom Scenario" }]);
  };

  const removeScenario = (index) => {
    if (scenarios.length > 1) {
      setScenarios(scenarios.filter((_, i) => i !== index));
    }
  };

  const toggleMilestoneCompletion = (index) => {
    const updatedMilestones = milestones.map((milestone, i) => 
      i === index ? { ...milestone, completed: !milestone.completed } : milestone
    );
    setMilestones(updatedMilestones);
  };

  const calculateGoalPlan = () => {
    const currencyRate = parseFloat(inputs.currency) || 1;
    const goalAmount = parseFloat(inputs.goalAmount) || 0;
    const currentSavings = parseFloat(inputs.currentSavings) || 0;
    const timeFrame = parseFloat(inputs.timeFrame) || 0;
    const monthlyContribution = parseFloat(inputs.monthlyContribution) || 0;
    const contributionIncrease = parseFloat(inputs.contributionIncrease) || 0;
    const expectedReturn = parseFloat(inputs.expectedReturn) || 0;
    const inflationRate = parseFloat(inputs.inflationRate) || 0;
    const initialContribution = parseFloat(inputs.initialContribution) || 0;
    const oneTimeContributions = parseFloat(inputs.oneTimeContributions) || 0;
    const probability = parseFloat(inputs.probability) || 0;

    // Calculate monthly return rate
    const monthlyReturn = expectedReturn / 100 / 12;
    const monthlyInflation = inflationRate / 100 / 12;

    let futureValue = currentSavings + initialContribution;
    const monthlyProjection = [];
    let totalContributions = currentSavings + initialContribution;
    let month = 0;

    // Calculate monthly projection
    while (month < timeFrame * 12 && futureValue < goalAmount) {
      if (month > 0) {
        // Apply investment return
        futureValue = futureValue * (1 + monthlyReturn);
        
        // Add monthly contribution with annual increase
        const currentMonthlyContribution = monthlyContribution * Math.pow(1 + contributionIncrease/100, Math.floor(month/12));
        futureValue += currentMonthlyContribution;
        totalContributions += currentMonthlyContribution;
        
        // Add one-time contributions at specified intervals
        if (oneTimeContributions > 0 && month % 12 === 0) {
          futureValue += oneTimeContributions;
          totalContributions += oneTimeContributions;
        }
      }

      monthlyProjection.push({
        month,
        year: Math.floor(month / 12),
        futureValue,
        contributions: totalContributions,
        earnings: futureValue - totalContributions,
        monthlyContribution: monthlyContribution * Math.pow(1 + contributionIncrease/100, Math.floor(month/12))
      });

      month++;
    }

    // Calculate required metrics
    const remainingAmount = Math.max(0, goalAmount - futureValue);
    const monthsToGoal = monthlyProjection.length;
    const yearsToGoal = monthsToGoal / 12;
    const goalDate = new Date();
    goalDate.setMonth(goalDate.getMonth() + monthsToGoal);

    // Calculate required monthly contribution to reach goal in time frame
    const requiredMonthly = calculateRequiredMonthlyContribution();

    // Calculate success probability
    const successProbability = calculateSuccessProbability();

    // Calculate inflation-adjusted goal
    const realGoalAmount = goalAmount / Math.pow(1 + inflationRate/100, timeFrame);

    // Calculate milestone progress
    const milestoneProgress = calculateMilestoneProgress(monthlyProjection, goalAmount, currencyRate);

    const resultData = {
      // Core calculations
      goalAmount: goalAmount * currencyRate,
      currentSavings: currentSavings * currencyRate,
      futureValue: futureValue * currencyRate,
      remainingAmount: remainingAmount * currencyRate,
      totalContributions: totalContributions * currencyRate,
      totalEarnings: (futureValue - totalContributions) * currencyRate,
      
      // Time metrics
      timeFrame,
      monthsToGoal,
      yearsToGoal: yearsToGoal,
      goalDate: goalDate.toISOString().split('T')[0],
      isAchievable: futureValue >= goalAmount,
      
      // Contribution analysis
      monthlyContribution: monthlyContribution * currencyRate,
      requiredMonthly: requiredMonthly * currencyRate,
      contributionIncrease,
      initialContribution: initialContribution * currencyRate,
      
      // Investment metrics
      expectedReturn,
      inflationRate,
      realGoalAmount: realGoalAmount * currencyRate,
      realReturn: ((1 + expectedReturn/100) / (1 + inflationRate/100) - 1) * 100,
      
      // Progress tracking
      progressPercentage: Math.min(100, (futureValue / goalAmount) * 100),
      milestoneProgress,
      monthlyProjection,
      
      // Risk analysis
      successProbability,
      riskTolerance: inputs.riskTolerance,
      
      // Additional details
      currencyRate,
      goalType,
      goalName: inputs.goalName,
      startDate: inputs.startDate
    };

    setResult(resultData);
  };

  const calculateRequiredMonthlyContribution = () => {
    const goalAmount = parseFloat(inputs.goalAmount) || 0;
    const currentSavings = parseFloat(inputs.currentSavings) || 0;
    const timeFrame = parseFloat(inputs.timeFrame) || 0;
    const expectedReturn = parseFloat(inputs.expectedReturn) || 0;
    const initialContribution = parseFloat(inputs.initialContribution) || 0;

    const monthlyReturn = expectedReturn / 100 / 12;
    const totalMonths = timeFrame * 12;
    
    // Future value of current savings and initial contribution
    const futureValueOfCurrent = (currentSavings + initialContribution) * Math.pow(1 + monthlyReturn, totalMonths);
    
    // Required future value from contributions
    const requiredFromContributions = goalAmount - futureValueOfCurrent;
    
    if (requiredFromContributions <= 0) return 0;
    
    // Calculate required monthly contribution using annuity formula
    const requiredMonthly = requiredFromContributions * monthlyReturn / (Math.pow(1 + monthlyReturn, totalMonths) - 1);
    
    return Math.max(0, requiredMonthly);
  };

  const calculateSuccessProbability = () => {
    const baseProbability = parseFloat(inputs.probability) || 0;
    const riskTolerance = inputs.riskTolerance;
    
    let riskAdjustment = 0;
    switch (riskTolerance) {
      case "conservative":
        riskAdjustment = 10;
        break;
      case "moderate":
        riskAdjustment = 0;
        break;
      case "aggressive":
        riskAdjustment = -15;
        break;
    }
    
    return Math.max(0, Math.min(100, baseProbability + riskAdjustment));
  };

  const calculateMilestoneProgress = (monthlyProjection, goalAmount, currencyRate) => {
    return milestones.map(milestone => {
      const target = parseFloat(milestone.target) || 0;
      const achievedMonth = monthlyProjection.find(month => month.futureValue >= target);
      
      return {
        ...milestone,
        target: target * currencyRate,
        achieved: !!achievedMonth,
        achievedMonth: achievedMonth ? achievedMonth.month : null,
        achievedDate: achievedMonth ? new Date(new Date().setMonth(new Date().getMonth() + achievedMonth.month)).toISOString().split('T')[0] : null,
        onTrack: calculateOnTrackStatus(milestone, monthlyProjection)
      };
    });
  };

  const calculateOnTrackStatus = (milestone, monthlyProjection) => {
    const target = parseFloat(milestone.target) || 0;
    const deadline = new Date(milestone.deadline);
    const today = new Date();
    const monthsToDeadline = (deadline.getFullYear() - today.getFullYear()) * 12 + (deadline.getMonth() - today.getMonth());
    
    if (monthsToDeadline <= 0) return milestone.completed ? 'completed' : 'behind';
    
    const projectedValue = monthlyProjection[Math.min(monthsToDeadline, monthlyProjection.length - 1)]?.futureValue || 0;
    
    if (projectedValue >= target) return 'ahead';
    if (projectedValue >= target * 0.8) return 'on-track';
    return 'behind';
  };

  const calculateScenarioAnalysis = () => {
    const goalAmount = parseFloat(inputs.goalAmount) || 0;
    const currentSavings = parseFloat(inputs.currentSavings) || 0;
    const monthlyContribution = parseFloat(inputs.monthlyContribution) || 0;
    const timeFrame = parseFloat(inputs.timeFrame) || 0;

    return scenarios.map(scenario => {
      const returnRate = parseFloat(scenario.return) || 0;
      const probability = parseFloat(scenario.probability) || 0;
      
      const monthlyReturn = returnRate / 100 / 12;
      const totalMonths = timeFrame * 12;
      
      // Calculate future value
      let futureValue = currentSavings;
      for (let month = 0; month < totalMonths; month++) {
        futureValue = futureValue * (1 + monthlyReturn) + monthlyContribution;
      }
      
      const goalAchieved = futureValue >= goalAmount;
      const surplusDeficit = futureValue - goalAmount;
      const achievementPercentage = (futureValue / goalAmount) * 100;
      const expectedValue = (futureValue * probability) / 100;
      
      return {
        ...scenario,
        futureValue,
        goalAchieved,
        surplusDeficit,
        achievementPercentage,
        expectedValue,
        monthsEarlyLate: goalAchieved ? calculateMonthsEarly(futureValue, returnRate, monthlyContribution, goalAmount) : 0
      };
    });
  };

  const calculateMonthsEarly = (futureValue, returnRate, monthlyContribution, goalAmount) => {
    const monthlyReturn = returnRate / 100 / 12;
    
    let months = 0;
    let value = futureValue;
    
    while (value > goalAmount && months < 600) { // Max 50 years
      value = value / (1 + monthlyReturn) - monthlyContribution;
      months++;
    }
    
    return months;
  };

  const calculateYearlyProgress = () => {
    if (!result) return [];

    const yearlyData = [];
    const years = Math.ceil(result.timeFrame);
    
    for (let year = 0; year <= years; year++) {
      const monthIndex = year * 12;
      const projection = result.monthlyProjection[Math.min(monthIndex, result.monthlyProjection.length - 1)];
      
      if (projection) {
        yearlyData.push({
          year,
          value: projection.futureValue,
          contributions: projection.contributions,
          earnings: projection.earnings,
          goalPercentage: (projection.futureValue / result.goalAmount) * 100
        });
      }
    }

    return yearlyData;
  };

  const resetCalculator = () => {
    setMilestones([
      { name: "Initial Setup", target: "5000", deadline: "2024-12-31", completed: true },
      { name: "Quarter 1 Target", target: "10000", deadline: "2025-03-31", completed: false },
      { name: "Halfway Point", target: "25000", deadline: "2025-12-31", completed: false },
      { name: "Final Goal", target: "50000", deadline: "2026-12-31", completed: false }
    ]);
    setScenarios([
      { return: "5", probability: "25", label: "Conservative" },
      { return: "7", probability: "50", label: "Moderate" },
      { return: "10", probability: "25", label: "Aggressive" }
    ]);
    setInputs({
      goalName: "My Financial Goal",
      goalAmount: "50000",
      currentSavings: "5000",
      timeFrame: "5",
      monthlyContribution: "500",
      contributionIncrease: "3",
      expectedReturn: "7",
      inflationRate: "2.5",
      initialContribution: "0",
      oneTimeContributions: "0",
      withdrawalRate: "4",
      riskTolerance: "moderate",
      probability: "75",
      milestone1: "25",
      milestone2: "50",
      milestone3: "75",
      currency: "1",
      startDate: new Date().toISOString().split('T')[0]
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Goal Name,${result.goalName}`,
      `Target Amount,${result.goalAmount.toFixed(2)}`,
      `Current Savings,${result.currentSavings.toFixed(2)}`,
      `Future Value,${result.futureValue.toFixed(2)}`,
      `Remaining Amount,${result.remainingAmount.toFixed(2)}`,
      `Time Frame,${result.timeFrame} years`,
      `Months to Goal,${result.monthsToGoal}`,
      `Expected Goal Date,${result.goalDate}`,
      `Monthly Contribution,${result.monthlyContribution.toFixed(2)}`,
      `Required Monthly,${result.requiredMonthly.toFixed(2)}`,
      `Expected Return,${result.expectedReturn}%`,
      `Success Probability,${result.successProbability}%`,
      `Progress,${result.progressPercentage.toFixed(2)}%`,
      `Total Contributions,${result.totalContributions.toFixed(2)}`,
      `Total Earnings,${result.totalEarnings.toFixed(2)}`,
      `Goal Type,${result.goalType}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'goal-plan.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      milestones: [...milestones],
      scenarios: [...scenarios],
      goalType,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Goal plan saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setMilestones(calc.milestones || []);
    setScenarios(calc.scenarios || []);
    setGoalType(calc.goalType || "savings");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Goal plan loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      emergency: { 
        goalType: "savings",
        inputs: { 
          goalName: "Emergency Fund",
          goalAmount: "15000", 
          currentSavings: "2000",
          timeFrame: "2",
          monthlyContribution: "500",
          expectedReturn: "3",
          riskTolerance: "conservative"
        }
      },
      retirement: { 
        goalType: "retirement",
        inputs: { 
          goalName: "Retirement Fund",
          goalAmount: "1000000", 
          currentSavings: "50000",
          timeFrame: "20",
          monthlyContribution: "1500",
          expectedReturn: "7",
          riskTolerance: "moderate"
        }
      },
      house: { 
        goalType: "house",
        inputs: { 
          goalName: "House Down Payment",
          goalAmount: "80000", 
          currentSavings: "10000",
          timeFrame: "5",
          monthlyContribution: "1000",
          expectedReturn: "5",
          riskTolerance: "conservative"
        }
      }
    };
    
    const presetData = presets[preset];
    setGoalType(presetData.goalType);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    addNotification(`${preset} goal preset applied`, "info");
  };

  // Chart Data with null checks
  const progressChartData = result ? {
    labels: ["Current Savings", "Future Contributions", "Investment Earnings"],
    datasets: [{
      data: [result.currentSavings, result.totalContributions - result.currentSavings, result.totalEarnings],
      backgroundColor: ["#19692C", "#FFD93D", "#4ECDC4"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : {
    labels: ["Current Savings", "Future Contributions", "Investment Earnings"],
    datasets: [{
      data: [0, 0, 0],
      backgroundColor: ["#19692C", "#FFD93D", "#4ECDC4"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  };

  const projectionData = result?.monthlyProjection || [];
  const projectionChartData = {
    labels: projectionData.length > 0 
      ? projectionData.filter((_, i) => i % 6 === 0 || i === projectionData.length - 1).map(d => `Month ${d.month}`)
      : ["Start"],
    datasets: [
      {
        label: 'Portfolio Value',
        data: projectionData.length > 0 
          ? projectionData.filter((_, i) => i % 6 === 0 || i === projectionData.length - 1).map(d => d.futureValue)
          : [0],
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Goal Target',
        data: projectionData.length > 0 
          ? projectionData.filter((_, i) => i % 6 === 0 || i === projectionData.length - 1).map(() => result?.goalAmount || 0)
          : [0],
        borderColor: "#FF6B6B",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  };

  const scenarioData = calculateScenarioAnalysis();
  const scenarioChartData = {
    labels: scenarioData.map(s => s.label),
    datasets: [
      {
        label: 'Future Value',
        data: scenarioData.map(s => s.futureValue),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Goal Amount',
        data: scenarioData.map(() => parseFloat(inputs.goalAmount) || 0),
        backgroundColor: "#FF6B6B",
        borderColor: "#FF6B6B",
        borderWidth: 2
      }
    ]
  };

  const milestoneChartData = {
    labels: result?.milestoneProgress?.map(m => m.name) || milestones.map(m => m.name),
    datasets: [{
      label: 'Target Amount',
      data: result?.milestoneProgress?.map(m => m.target) || milestones.map(m => parseFloat(m.target) || 0),
      backgroundColor: (result?.milestoneProgress || milestones).map(m => 
        m.completed ? '#28A745' : 
        m.onTrack === 'ahead' ? '#4ECDC4' :
        m.onTrack === 'on-track' ? '#FFD93D' : '#FF6B6B'
      ),
      borderColor: (result?.milestoneProgress || milestones).map(m => 
        m.completed ? '#28A745' : 
        m.onTrack === 'ahead' ? '#4ECDC4' :
        m.onTrack === 'on-track' ? '#FFD93D' : '#FF6B6B'
      ),
      borderWidth: 2
    }]
  };

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Financial Goal Planner</h1>
        </div>
        <p>Plan, track, and achieve your financial goals with detailed projections and milestone tracking</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Goal Plans</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved goal plans yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.goalName}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.isAchievable ? 'positive' : 'negative'}`}>
                        {calc.progressPercentage?.toFixed(1)}%
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Planner' : 'Goal Planner'}
              </button>
              <button 
                className={`tab ${activeTab === "milestones" ? "active" : ""}`}
                onClick={() => setActiveTab("milestones")}
              >
                <i className="fas fa-flag"></i>
                {isMobile ? 'Milestones' : 'Milestones'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Goal Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('emergency')}>
                Emergency Fund
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('retirement')}>
                Retirement
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('house')}>
                House Fund
              </button>
            </div>
          </div>

          {/* Goal Type Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${goalType === "savings" ? "active" : ""}`}
                onClick={() => setGoalType("savings")}
              >
                <i className="fas fa-piggy-bank"></i>
                Savings Goal
              </button>
              <button 
                className={`mode-btn ${goalType === "retirement" ? "active" : ""}`}
                onClick={() => setGoalType("retirement")}
              >
                <i className="fas fa-umbrella-beach"></i>
                Retirement
              </button>
              <button 
                className={`mode-btn ${goalType === "education" ? "active" : ""}`}
                onClick={() => setGoalType("education")}
              >
                <i className="fas fa-graduation-cap"></i>
                Education
              </button>
              <button 
                className={`mode-btn ${goalType === "house" ? "active" : ""}`}
                onClick={() => setGoalType("house")}
              >
                <i className="fas fa-home"></i>
                House Fund
              </button>
            </div>
          </div>

          {/* Goal Planner Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Basic Goal Information */}
                  <h3>
                    <i className="fas fa-bullseye"></i>
                    Goal Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-tag"></i> Goal Name</label>
                      <input 
                        type="text" 
                        name="goalName" 
                        value={inputs.goalName} 
                        onChange={handleChange}
                        placeholder="My Financial Goal"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Target Amount</label>
                      <input 
                        type="number" 
                        name="goalAmount" 
                        value={inputs.goalAmount} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-piggy-bank"></i> Current Savings</label>
                      <input 
                        type="number" 
                        name="currentSavings" 
                        value={inputs.currentSavings} 
                        onChange={handleChange}
                        placeholder="5000"
                      />
                    </div>
                  </div>

                  {/* Time Frame */}
                  <h3>
                    <i className="fas fa-calendar"></i>
                    Time Frame
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Time Frame (Years)</label>
                      <input 
                        type="number" 
                        name="timeFrame" 
                        value={inputs.timeFrame} 
                        onChange={handleChange}
                        placeholder="5"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Start Date</label>
                      <input 
                        type="date" 
                        name="startDate" 
                        value={inputs.startDate} 
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Contributions */}
                  <h3>
                    <i className="fas fa-money-bill-wave"></i>
                    Contributions
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Monthly Contribution</label>
                      <input 
                        type="number" 
                        name="monthlyContribution" 
                        value={inputs.monthlyContribution} 
                        onChange={handleChange}
                        placeholder="500"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Annual Increase (%)</label>
                      <input 
                        type="number" 
                        name="contributionIncrease" 
                        value={inputs.contributionIncrease} 
                        onChange={handleChange}
                        placeholder="3"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Initial Contribution</label>
                      <input 
                        type="number" 
                        name="initialContribution" 
                        value={inputs.initialContribution} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Investment Settings */}
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Investment Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expected Return (%)</label>
                      <input 
                        type="number" 
                        name="expectedReturn" 
                        value={inputs.expectedReturn} 
                        onChange={handleChange}
                        placeholder="7"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Risk Tolerance</label>
                      <select name="riskTolerance" value={inputs.riskTolerance} onChange={handleChange}>
                        <option value="conservative">Conservative</option>
                        <option value="moderate">Moderate</option>
                        <option value="aggressive">Aggressive</option>
                      </select>
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Annual Bonus/Extra</label>
                      <input 
                        type="number" 
                        name="oneTimeContributions" 
                        value={inputs.oneTimeContributions} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Success Probability (%)</label>
                      <input 
                        type="number" 
                        name="probability" 
                        value={inputs.probability} 
                        onChange={handleChange}
                        placeholder="75"
                        step="1"
                      />
                    </div>
                  </div>

                  {/* Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Goal Plan
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Milestones Tab */}
          {activeTab === "milestones" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-flag"></i>
                  Goal Milestones
                </h3>
                
                {milestones.map((milestone, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <div className="milestone-checkbox">
                        <input 
                          type="checkbox"
                          checked={milestone.completed}
                          onChange={() => toggleMilestoneCompletion(index)}
                          className="checkbox-input"
                        />
                        <label className="checkbox-label">
                          Milestone #{index + 1}
                        </label>
                      </div>
                      {milestones.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeMilestone(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Milestone Name</label>
                        <input 
                          type="text" 
                          value={milestone.name}
                          onChange={(e) => handleMilestoneChange(index, 'name', e.target.value)}
                          placeholder="Milestone Name"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Target Amount</label>
                        <input 
                          type="number" 
                          value={milestone.target}
                          onChange={(e) => handleMilestoneChange(index, 'target', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-calendar"></i> Deadline</label>
                        <input 
                          type="date" 
                          value={milestone.deadline}
                          onChange={(e) => handleMilestoneChange(index, 'deadline', e.target.value)}
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addMilestone}>
                  <i className="fas fa-plus"></i>
                  Add Another Milestone
                </button><br/>

                {/* Scenarios Section */}
                <h3>
                  <i className="fas fa-chess-board"></i>
                  Return Scenarios
                </h3>
                
                {scenarios.map((scenario, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>{scenario.label} Scenario</h4>
                      {scenarios.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeScenario(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Expected Return (%)</label>
                        <input 
                          type="number" 
                          value={scenario.return}
                          onChange={(e) => handleScenarioChange(index, 'return', e.target.value)}
                          placeholder="7"
                          step="0.1"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-chart-pie"></i> Probability (%)</label>
                        <input 
                          type="number" 
                          value={scenario.probability}
                          onChange={(e) => handleScenarioChange(index, 'probability', e.target.value)}
                          placeholder="50"
                          step="1"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Scenario Label</label>
                        <input 
                          type="text" 
                          value={scenario.label}
                          onChange={(e) => handleScenarioChange(index, 'label', e.target.value)}
                          placeholder="Scenario Name"
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addScenario}>
                  <i className="fas fa-plus"></i>
                  Add Another Scenario
                </button>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Goal Plans
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No goal plans yet. Start planning to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.isAchievable ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.isAchievable ? 'fa-check' : 'fa-times'}`}></i>
                          {calc.progressPercentage?.toFixed(1)}%
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-bullseye"></i> {calc.goalName}</span>
                        <span><i className="fas fa-dollar-sign"></i> Target: ${calc.goalAmount?.toFixed(2)}</span>
                        <span><i className="fas fa-calendar"></i> {calc.timeFrame} years</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "milestones") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-bullseye"></i>
                    Goal Progress Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className={`result-card highlight ${result.isAchievable ? 'positive' : 'negative'}`}>
                        <div className={`result-value ${result.isAchievable ? 'positive' : 'negative'}`}>
                          {result.progressPercentage.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Goal Progress
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.futureValue.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Projected Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.yearsToGoal.toFixed(1)} yrs
                        </div>
                        <div className="result-label">
                          <i className="fas fa-clock"></i>
                          Time to Goal
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.successProbability}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-shield-alt"></i>
                          Success Probability
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Goal Name:</span>
                        <span>{result.goalName}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Target Amount:</span>
                        <span>${result.goalAmount.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Current Savings:</span>
                        <span>${result.currentSavings.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Monthly Contribution:</span>
                        <span>${result.monthlyContribution.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Expected Goal Date:</span>
                        <span>{result.goalDate}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Expected Return:</span>
                        <span>{result.expectedReturn}%</span>
                      </div>
                    </div>

                    {/* Goal Status */}
                    <div className="risk-summary">
                      <h4>Goal Status</h4>
                      <div className="risk-cards">
                        <div className={`risk-card ${result.isAchievable ? 'positive' : 'negative'}`}>
                          <div className={`risk-value ${result.isAchievable ? 'positive' : 'negative'}`}>
                            {result.isAchievable ? 'ACHIEVABLE' : 'AT RISK'}
                          </div>
                          <div className="risk-label">Goal Status</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.remainingAmount.toFixed(2)}
                          </div>
                          <div className="risk-label">Remaining Amount</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.requiredMonthly.toFixed(2)}
                          </div>
                          <div className="risk-label">Required Monthly</div>
                        </div>
                      </div>
                    </div>

                    {/* Progress Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Progress Composition
                      </h3>
                      <Doughnut 
                        data={progressChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Projection Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Goal Progress Projection
                      </h3>
                      <Line 
                        data={projectionChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Yearly Progress Table */}
                    <div className="risk-summary">
                      <h4>Yearly Progress</h4>
                      <div className="projection-table">
                        <div className="table-header">
                          <span>Year</span>
                          <span>Portfolio Value</span>
                          <span>Contributions</span>
                          <span>Goal Progress</span>
                        </div>
                        {calculateYearlyProgress().map((year, index) => (
                          <div key={index} className="table-row">
                            <span>{year.year}</span>
                            <span>${year.value.toFixed(2)}</span>
                            <span>${year.contributions.toFixed(2)}</span>
                            <span className={year.goalPercentage >= 100 ? 'positive' : ''}>
                              {year.goalPercentage.toFixed(1)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-bullseye fa-3x"></i>
                    <p>Enter your goal details to create a financial plan</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "milestones" && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-flag"></i>
                  Milestone Tracking
                </h3>
                
                <div className="milestones-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      {result ? result.milestoneProgress.filter(m => m.completed).length : milestones.filter(m => m.completed).length}/{result ? result.milestoneProgress.length : milestones.length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-flag"></i>
                      Milestones Completed
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result ? result.milestoneProgress.filter(m => m.onTrack === 'ahead' || m.onTrack === 'on-track').length : 0}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-check"></i>
                      On Track
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${Math.max(...(result ? result.milestoneProgress.map(m => m.target) : milestones.map(m => parseFloat(m.target) || 0))).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bullseye"></i>
                      Largest Milestone
                    </div>
                  </div><br/>
                </div>

                {/* Milestone Progress Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Milestone Targets
                  </h3>
                  <Bar 
                    data={milestoneChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Scenario Analysis Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chess-board"></i>
                    Scenario Analysis
                  </h3>
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Detailed Milestone Progress */}
                <div className="milestones-details">
                  <h4>Milestone Details</h4><br/>
                  <div className="milestones-list">
                    {(result ? result.milestoneProgress : milestones).map((milestone, index) => (
                      <div key={index} className={`milestone-item ${milestone.completed ? 'completed' : milestone.onTrack || 'behind'}`}>
                        <div className="milestone-header">
                          <div className="milestone-info">
                            <div className="milestone-name">
                              {milestone.completed && <i className="fas fa-check completed-icon"></i>}
                              {milestone.name}
                            </div>
                            <div className="milestone-target">${milestone.target.toFixed(2)}</div>
                          </div>
                          <div className={`milestone-status ${milestone.completed ? 'completed' : milestone.onTrack || 'behind'}`}>
                            {milestone.completed ? 'COMPLETED' : 
                             milestone.onTrack === 'ahead' ? 'AHEAD' :
                             milestone.onTrack === 'on-track' ? 'ON TRACK' : 'BEHIND'}
                          </div>
                        </div>
                        <div className="milestone-details">
                          <div className="milestone-metric">
                            <span>Deadline:</span>
                            <span>{milestone.deadline}</span>
                          </div>
                          {milestone.achievedDate && (
                            <div className="milestone-metric">
                              <span>Achieved:</span>
                              <span>{milestone.achievedDate}</span>
                            </div>
                          )}
                          {!milestone.completed && milestone.achievedDate && (
                            <div className="milestone-metric">
                              <span>Status:</span>
                              <span>{milestone.achievedDate < milestone.deadline ? 'Early' : 'Late'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div><br/>
                </div>

                {/* Scenario Analysis */}
                <div className="scenario-analysis">
                  <h4>Return Scenario Analysis</h4><br/>
                  <div className="scenario-list">
                    {scenarioData.map((scenario, index) => (
                      <div key={index} className={`scenario-item ${scenario.goalAchieved ? 'positive' : 'negative'}`}>
                        <div className="scenario-header">
                          <span className="scenario-name">{scenario.label}</span>
                          <span className="scenario-return">{scenario.return}% Return</span>
                        </div>
                        <div className="scenario-details">
                          <div className="scenario-metric">
                            <span>Future Value:</span>
                            <span>${scenario.futureValue.toFixed(2)}</span>
                          </div>
                          <div className="scenario-metric">
                            <span>Goal Status:</span>
                            <span className={scenario.goalAchieved ? 'positive' : 'negative'}>
                              {scenario.goalAchieved ? 'ACHIEVED' : 'NOT ACHIEVED'}
                            </span>
                          </div>
                          <div className="scenario-metric">
                            <span>Surplus/Deficit:</span>
                            <span className={scenario.surplusDeficit >= 0 ? 'positive' : 'negative'}>
                              ${scenario.surplusDeficit.toFixed(2)}
                            </span>
                          </div>
                          <div className="scenario-metric">
                            <span>Achievement:</span>
                            <span>{scenario.achievementPercentage.toFixed(1)}%</span>
                          </div>
                          <div className="scenario-metric">
                            <span>Probability:</span>
                            <span>{scenario.probability}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div><br/>
                </div>

                <div className="goal-tips">
                  <h4>Goal Achievement Tips</h4><br/>
                  <div className="tips-list">
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Automate your contributions to ensure consistent savings</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Review and adjust your plan quarterly based on progress</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Consider increasing contributions with salary raises</span>
                    </div>
                    <div className="tip-item warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Diversify investments to manage risk while pursuing returns</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Celebrate milestone achievements to maintain motivation</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default GoalPlannerCalculator;